"use client";

/**
 * Toast store. Six intents, dedupeKey and progress handles MORPH in place.
 * The Toaster shows one bar (errors first, then oldest) and owns the timers, so a
 * queued bar's clock starts when it shows and hover or focus pauses it.
 */

import * as React from "react";

import type { ToastIntent } from "@/components/shared/ui/toast";

/** One visible plus two waiting. */
export const TOAST_LIMIT = 3;
// Exit fade is 120ms; the next bar shows after removal.
export const TOAST_REMOVE_DELAY = 150;

// design.md (10-16): success, warning, info 10s (Leo); undo is the server window;
// errors and progress stay until closed or resolved.
export const INTENT_DURATION_MS: Record<ToastIntent, number | null> = {
  progress: null,
  success: 10000,
  successUndoable: 8000,
  warning: 10000,
  error: null,
  info: 10000,
};

// Length budgets. We don't truncate — we `console.warn` in dev so copy owners
// see the problem without a silent layout break in prod.
const MAX_HEADING_CHARS = 40;
const MAX_BODY_CHARS = 100;

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export interface ToastActionDescriptor {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastFixDescriptor {
  label: string;
  to: string;
}

/** Internal toast state. `open` is the Radix contract that drives the exit fade. */
export interface ToasterToast {
  id: string;
  intent: ToastIntent;
  heading: React.ReactNode;
  body?: React.ReactNode;
  /** Same key while open: morph instead of queue. */
  dedupeKey?: string;
  /** null: stays until closed or resolved. */
  durationMs?: number | null;
  undo?: {
    onUndo: () => Promise<void> | void;
    windowMs: number;
    startedAt: number;
  };
  action?: ToastActionDescriptor;
  retry?: () => Promise<void> | void;
  fix?: ToastFixDescriptor;
  open: boolean;
  /** Bumped on every MORPH; the Toaster keys the row by `id:rev`. */
  rev?: number;
  onOpenChange?: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  /** Same as UPDATE plus a `rev` bump: the row remounts, restarts its timer and is heard again. */
  MORPH_TOAST: "MORPH_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | { type: ActionType["ADD_TOAST"]; toast: ToasterToast }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast> & { id: string };
    }
  | {
      type: ActionType["MORPH_TOAST"];
      toast: Partial<ToasterToast> & { id: string };
    }
  | { type: ActionType["DISMISS_TOAST"]; toastId?: ToasterToast["id"] }
  | { type: ActionType["REMOVE_TOAST"]; toastId?: ToasterToast["id"] };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) return;
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: "REMOVE_TOAST", toastId });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};

/** Open toasts in show order: errors first, then oldest first. Index 0 is the visible bar. */
export function queueOrder(toasts: ToasterToast[]): ToasterToast[] {
  const open = toasts.filter((t) => t.open);
  return [
    ...open.filter((t) => t.intent === "error"),
    ...open.filter((t) => t.intent !== "error"),
  ];
}

/** Over TOAST_LIMIT, drop the oldest waiting non-error (an error only if all are), never the visible bar. */
function enforceStackPolicy(toasts: ToasterToast[]): ToasterToast[] {
  const ordered = queueOrder(toasts);
  if (ordered.length <= TOAST_LIMIT) return toasts;
  const waiting = ordered.slice(1);
  const dropped = waiting.find((t) => t.intent !== "error") ?? waiting[0];
  return toasts.filter((t) => t.id !== dropped.id);
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      const next = [...state.toasts, action.toast];
      return { ...state, toasts: enforceStackPolicy(next) };
    }

    case "UPDATE_TOAST":
    case "MORPH_TOAST": {
      const morph = action.type === "MORPH_TOAST";
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id
            ? { ...t, ...action.toast, rev: (t.rev ?? 0) + (morph ? 1 : 0) }
            : t,
        ),
      };
    }

    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId) addToRemoveQueue(toastId);
      else state.toasts.forEach((t) => addToRemoveQueue(t.id));
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined ? { ...t, open: false } : t,
        ),
      };
    }

    case "REMOVE_TOAST":
      if (action.toastId === undefined) return { ...state, toasts: [] };
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };

    default:
      return state;
  }
};

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ToastBase {
  heading: string;
  body?: string;
  dedupeKey?: string;
}

export interface ToastSuccessArgs extends ToastBase {
  durationMs?: number;
}
export interface ToastInfoArgs extends ToastBase {
  durationMs?: number;
}
export interface ToastWarningArgs extends ToastBase {
  action?: ToastActionDescriptor;
  durationMs?: number;
}
export interface ToastErrorArgs extends ToastBase {
  retry?: () => Promise<void> | void;
  fix?: ToastFixDescriptor;
}
/**
 * Undoable success — fired after a destructive action succeeds optimistically.
 *
 * `windowMs`:
 *   - Default 8000 (spec: 8s).
 *   - Use 10000 (10s) for single destructive row-level actions.
 *   - Use 12000 (12s) for bulk operations ≥10 items.
 *   - Use 15000 (15s) when the action TOUCHES a CBL row, an attestation
 *     record, or an RDP signature — these carry compliance/audit weight
 *     and deserve extra time for a human to reverse a mistake.
 */
export interface ToastSuccessUndoableArgs extends ToastBase {
  onUndo: () => Promise<void> | void;
  windowMs?: number;
}
export interface ToastProgressArgs extends ToastBase {}

export interface ToastHandle {
  id: string;
  resolveSuccess(opts: { heading: string; body?: string }): void;
  resolveSuccessUndoable(opts: {
    heading: string;
    body?: string;
    onUndo: () => Promise<void> | void;
    windowMs?: number;
  }): void;
  resolveError(opts: {
    heading: string;
    body?: string;
    retry?: () => Promise<void> | void;
    fix?: ToastFixDescriptor;
  }): void;
  update(opts: { heading?: string; body?: string }): void;
  dismiss(): void;
}

/**
 * Dev-mode length check. We don't throw — copy owners need runtime surfaces
 * to catch slow copy drift without PR blockers.
 */
function warnIfOverflow(intent: ToastIntent, heading: string, body?: string) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
  if (typeof heading === "string" && heading.length > MAX_HEADING_CHARS) {
    // eslint-disable-next-line no-console
    console.warn(
      `[toast] heading exceeds ${MAX_HEADING_CHARS} chars (intent=${intent}):`,
      heading,
    );
  }
  if (typeof body === "string" && body.length > MAX_BODY_CHARS) {
    // eslint-disable-next-line no-console
    console.warn(
      `[toast] body exceeds ${MAX_BODY_CHARS} chars (intent=${intent}):`,
      body,
    );
  }
  if (
    typeof heading === "string" &&
    typeof body === "string" &&
    heading.trim().toLowerCase() === body.trim().toLowerCase()
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[toast] body duplicates heading — drop the body prop (intent=${intent}).`,
    );
  }
}

function addToast(
  partial: Omit<ToasterToast, "id" | "open" | "onOpenChange">,
): ToastHandle {
  // An open toast with the same dedupeKey morphs in place and keeps its id, close and handle.
  const existing = partial.dedupeKey
    ? memoryState.toasts.find(
        (t) => t.open && t.dedupeKey === partial.dedupeKey,
      )
    : undefined;
  const id = existing?.id ?? genId();
  const full: ToasterToast = {
    undo: undefined,
    action: undefined,
    retry: undefined,
    fix: undefined,
    ...partial,
    durationMs:
      partial.durationMs !== undefined
        ? partial.durationMs
        : INTENT_DURATION_MS[partial.intent],
    id,
    open: true,
    onOpenChange: (open) => {
      if (!open) dispatch({ type: "DISMISS_TOAST", toastId: id });
    },
  };
  dispatch({ type: existing ? "MORPH_TOAST" : "ADD_TOAST", toast: full });
  return makeHandle(id);
}

function makeHandle(id: string): ToastHandle {
  return {
    id,
    resolveSuccess({ heading, body }) {
      warnIfOverflow("success", heading, body);
      dispatch({
        type: "MORPH_TOAST",
        toast: {
          id,
          intent: "success",
          heading,
          body,
          durationMs: INTENT_DURATION_MS.success,
          // Clear any progress-stage descriptors.
          undo: undefined,
          retry: undefined,
          fix: undefined,
          action: undefined,
        },
      });
    },
    resolveSuccessUndoable({ heading, body, onUndo, windowMs = 8000 }) {
      warnIfOverflow("successUndoable", heading, body);
      dispatch({
        type: "MORPH_TOAST",
        toast: {
          id,
          intent: "successUndoable",
          heading,
          body,
          durationMs: windowMs,
          undo: { onUndo, windowMs, startedAt: Date.now() },
          retry: undefined,
          fix: undefined,
          action: undefined,
        },
      });
    },
    resolveError({ heading, body, retry, fix }) {
      warnIfOverflow("error", heading, body);
      if (
        typeof import.meta !== "undefined" &&
        import.meta.env?.DEV &&
        !retry &&
        !fix
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          "[toast] error resolved without retry or fix. Users will have no affordance beyond dismissing.",
        );
      }
      dispatch({
        type: "MORPH_TOAST",
        toast: {
          id,
          intent: "error",
          heading,
          body,
          durationMs: null,
          retry,
          fix,
          undo: undefined,
          action: undefined,
        },
      });
    },
    update({ heading, body }) {
      dispatch({
        type: "UPDATE_TOAST",
        toast: { id, heading, body },
      });
    },
    dismiss() {
      dispatch({ type: "DISMISS_TOAST", toastId: id });
    },
  };
}

// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------

export interface ToastApi {
  (args: LegacyToastArgs): ToastHandle;
  progress: (args: ToastProgressArgs) => ToastHandle;
  success: (args: ToastSuccessArgs) => void;
  successUndoable: (args: ToastSuccessUndoableArgs) => void;
  warning: (args: ToastWarningArgs) => void;
  error: (args: ToastErrorArgs) => void;
  info: (args: ToastInfoArgs) => void;
  dismiss: (toastId?: string) => void;
}

// ---------------------------------------------------------------------------
// Back-compat shim
// ---------------------------------------------------------------------------

/**
 * Legacy `{ title, description, variant }`: destructive is error, else info; title is the
 * heading, description the body, and a lone description the heading. Both run on one row.
 */
export type LegacyToastArgs =
  | {
      intent?: never;
      title?: React.ReactNode;
      description?: React.ReactNode;
      variant?: "default" | "destructive" | null;
      dedupeKey?: string;
    }
  | {
      intent: ToastIntent;
      heading: string;
      body?: string;
      dedupeKey?: string;
      durationMs?: number | null;
    };

function warnLegacy(argsDump: string) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
  // eslint-disable-next-line no-console
  console.warn(
    "[toast-migration] legacy call shape used. Migrate to toast.success / toast.error / … — see TODO(toast-migration).",
    argsDump,
    new Error("toast legacy call stack").stack,
  );
}

function legacyToast(args: LegacyToastArgs): ToastHandle {
  // New shape — fast path, no shim.
  if ("intent" in args && args.intent) {
    return addToast({
      intent: args.intent,
      heading: args.heading as React.ReactNode,
      body: args.body,
      dedupeKey: args.dedupeKey,
      durationMs: args.durationMs,
    });
  }
  // Legacy shape.
  const legacy = args as Exclude<LegacyToastArgs, { intent: ToastIntent }>;
  const variant = legacy.variant ?? "default";
  const isError = variant === "destructive";
  const intent: ToastIntent = isError ? "error" : "info";
  warnLegacy(
    JSON.stringify({
      title: typeof legacy.title === "string" ? legacy.title : "[node]",
      variant,
    }),
  );
  return addToast({
    intent,
    heading:
      (legacy.title as React.ReactNode) ??
      (legacy.description as React.ReactNode) ??
      "",
    body:
      legacy.title != null
        ? (legacy.description as React.ReactNode)
        : undefined,
    dedupeKey: legacy.dedupeKey,
  });
}

// Flat API — attach intent-specific helpers to the callable root.
const toast = legacyToast as ToastApi;

toast.progress = function progress({ heading, body, dedupeKey }) {
  warnIfOverflow("progress", heading, body);
  return addToast({
    intent: "progress",
    heading,
    body,
    dedupeKey,
    durationMs: null,
  });
};

toast.success = function success({ heading, body, dedupeKey, durationMs }) {
  warnIfOverflow("success", heading, body);
  addToast({
    intent: "success",
    heading,
    body,
    dedupeKey,
    durationMs: durationMs ?? INTENT_DURATION_MS.success,
  });
};

toast.successUndoable = function successUndoable({
  heading,
  body,
  onUndo,
  windowMs = 8000,
  dedupeKey,
}) {
  warnIfOverflow("successUndoable", heading, body);
  addToast({
    intent: "successUndoable",
    heading,
    body,
    dedupeKey,
    durationMs: windowMs,
    undo: { onUndo, windowMs, startedAt: Date.now() },
  });
};

toast.warning = function warning({
  heading,
  body,
  action,
  dedupeKey,
  durationMs,
}) {
  warnIfOverflow("warning", heading, body);
  addToast({
    intent: "warning",
    heading,
    body,
    action,
    dedupeKey,
    durationMs: durationMs ?? INTENT_DURATION_MS.warning,
  });
};

toast.error = function error({ heading, body, retry, fix, dedupeKey }) {
  warnIfOverflow("error", heading, body);
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env?.DEV &&
    !retry &&
    !fix
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      "[toast] error without retry or fix — user has no recovery affordance.",
    );
  }
  addToast({
    intent: "error",
    heading,
    body,
    retry,
    fix,
    dedupeKey,
    durationMs: null,
  });
};

toast.info = function info({ heading, body, dedupeKey, durationMs }) {
  warnIfOverflow("info", heading, body);
  addToast({
    intent: "info",
    heading,
    body,
    dedupeKey,
    durationMs: durationMs ?? INTENT_DURATION_MS.info,
  });
};

toast.dismiss = function dismiss(toastId?: string) {
  dispatch({ type: "DISMISS_TOAST", toastId });
};

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: toast.dismiss,
  };
}

// ---------------------------------------------------------------------------
// Reduced-motion hook — JS-gated so we don't depend on CSS alone (partial
// fallback can leave the morph mid-state).
// ---------------------------------------------------------------------------

export function useReducedMotion() {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);
  return reduced;
}

export { useToast, toast };
